/* eslint-disable max-lines */
import { Col, Row } from 'antd';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Card } from 'src/components/Card/Card';
import { DataView } from 'src/components/DataView/DataView';
import { ResourceNameView } from 'src/components/ResourceNameView/ResourceNameView';
import {
    CreateFinalOfferRequestInstallmentOptionEnum,
    LoanApplicationDto,
} from 'src/generated-api-client';
import { AgentApplicationStatus } from 'src/stores/AgentLoanApplicationsStore/AgentLoanApplicationsStore.types';
import { InstallmentOptionResourceStore } from 'src/stores/Resources/InstallmentOptionResource';
import { PaymentFreequencyResourceStore } from 'src/stores/Resources/PaymentFreequencyResourceStore';

type AgentLoanApplicationFinalOfferProps = {
    application?: LoanApplicationDto;
};
export const AgentLoanApplicationFinalOffer = observer(
    ({ application }: AgentLoanApplicationFinalOfferProps) => {
        const isVisible =
            application?.status === AgentApplicationStatus.ACTIVE ||
            application?.status === AgentApplicationStatus.FULLY_PAID;

        const finalOffer = application?.finalOffer;

        if (!isVisible) {
            return null;
        }

        return (
            <Card title={t('Agent.LoanApplication.FinalOffer.Title')}>
                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t('Agent.LoanApplication.FinalOffer.Name')}
                            value={finalOffer?.name}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.AgreementNumber',
                            )}
                            value={finalOffer?.agreementNumber}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.FinancingAmount',
                            )}
                            value={finalOffer?.financingAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.FirstPaymentDate',
                            )}
                            value={finalOffer?.firstPaymentDate}
                            formatValue="date"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.FinalSettlementDate',
                            )}
                            value={finalOffer?.finalSettlementDate}
                            formatValue="date"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <ResourceNameView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.PaymentFrequency',
                            )}
                            resourceStore={PaymentFreequencyResourceStore}
                            value={finalOffer?.paymentFrequency}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <ResourceNameView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.InstallmentOption',
                            )}
                            resourceStore={InstallmentOptionResourceStore}
                            value={finalOffer?.installmentOption}
                        />
                    </Col>
                    {finalOffer?.installmentOption ===
                        CreateFinalOfferRequestInstallmentOptionEnum.FixedValue && (
                        <Col xs={24} sm={12} md={12}>
                            <DataView
                                label={t(
                                    'Agent.LoanApplication.FinalOffer.InstallmentAmount',
                                )}
                                value={finalOffer?.financingAmount}
                                formatValue="currency-sar"
                            />
                        </Col>
                    )}
                    {finalOffer?.installmentOption ===
                        CreateFinalOfferRequestInstallmentOptionEnum.Percentage && (
                        <>
                            <Col xs={24} sm={12} md={12}>
                                <DataView
                                    label={t(
                                        'Agent.LoanApplication.FinalOffer.InstallmentAmount',
                                    )}
                                    value={finalOffer?.financingAmount}
                                    formatValue="percent"
                                />
                            </Col>
                            <Col xs={24} sm={12} md={12}>
                                <DataView
                                    label={t(
                                        'Agent.LoanApplication.FinalOffer.DeductionRatePct',
                                    )}
                                    value={finalOffer?.deductionRatePct}
                                    formatValue="percent"
                                />
                            </Col>
                        </>
                    )}
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.HoldbackRate',
                            )}
                            value={finalOffer?.holdbackRatePct}
                            formatValue="percent"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.MinimumInstallmentAmount',
                            )}
                            value={finalOffer?.minInstallmentAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.MaximumInstallmentAmount',
                            )}
                            value={finalOffer?.maxInstallmentAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.FinancingTerm',
                            )}
                            value={finalOffer?.term}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.MurabahaRate',
                            )}
                            value={finalOffer?.murabahaRatePct}
                            formatValue="percent"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.TotalMurabahaAmount',
                            )}
                            value={finalOffer?.totalMurabahaAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.TotalRepayableAmount',
                            )}
                            value={finalOffer?.totalRepayableAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.DisbursementAmount',
                            )}
                            value={finalOffer?.disbursementAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.AdminFeeRate',
                            )}
                            value={finalOffer?.adminFeeRatePct}
                            formatValue="percent"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.AdminFeeAmount',
                            )}
                            value={finalOffer?.adminFeeAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.AdminFeeAmountWithVat',
                            )}
                            value={finalOffer?.adminFeeWithVatAmount}
                            formatValue="currency-sar"
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t('Agent.LoanApplication.FinalOffer.IBAN')}
                            value={finalOffer?.iban}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <DataView
                            label={t(
                                'Agent.LoanApplication.FinalOffer.CollectionCommissionPct',
                            )}
                            value={finalOffer?.collectionCommissionPct}
                            formatValue="percent"
                        />
                    </Col>
                </Row>
            </Card>
        );
    },
);
